
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Article, Collection, CostParams, CostBreakdown } from './types.ts';
import api from './services/api.ts';
import geminiService from './services/geminiService.ts';
import { calculateCostBreakdown } from './services/costCalculator.ts';
import { useGoogleAnalytics, trackEvent } from './hooks/useGoogleAnalytics.ts';


// Component Imports
import Loader from './components/Loader.tsx';
import Header from './components/Header.tsx';
import ArticlePanel from './components/ArticlePanel.tsx';
import CollectionPanel from './components/CollectionPanel.tsx';
import TransferControls from './components/TransferControls.tsx';
import CostingPanel from './components/CostingPanel.tsx';
import ResultsPanel from './components/ResultsPanel.tsx';
import SaveCollectionDialog from './components/SaveCollectionDialog.tsx';
import GeminiAdvisor from './components/GeminiAdvisor.tsx';
import CostDetailModal from './components/CostDetailModal.tsx';
import CostSimulator from './components/CostSimulator.tsx';

type ActiveTab = 'collection' | 'costs' | 'advisor';

const TabButton: React.FC<{ label: string; icon: string; isActive: boolean; onClick: () => void; }> = ({ label, icon, isActive, onClick }) => (
  <button 
    onClick={onClick}
    className={`flex-1 text-center px-4 py-2 rounded-md transition-colors duration-200 flex items-center justify-center gap-2 font-medium ${
      isActive 
        ? 'bg-sky-600 text-white shadow' 
        : 'bg-transparent text-gray-600 hover:bg-sky-100 hover:text-sky-700'
    }`}
  >
    <i className={`fas ${icon} w-5 text-center`}></i>
    <span>{label}</span>
  </button>
);


function App() {
  // Inizializza Google Analytics
  useGoogleAnalytics();

  // === STATE MANAGEMENT ===
  const [isLoading, setIsLoading] = useState(true);
  const [allArticles, setAllArticles] = useState<Article[]>([]);
  const [allCollections, setAllCollections] = useState<Collection[]>([]);
  const [selectedCollectionId, setSelectedCollectionId] = useState<string>('new');
  const [currentCollectionArticles, setCurrentCollectionArticles] = useState<Article[]>([]);
  const [originalCollectionArticles, setOriginalCollectionArticles] = useState<string[]>([]);
  const [inspectedArticleId, setInspectedArticleId] = useState<string | null>(null);
  const [costModalState, setCostModalState] = useState<{ article: Article, breakdown: CostBreakdown } | null>(null);

  const [activeTab, setActiveTab] = useState<ActiveTab>('collection');
  
  const [costParams, setCostParams] = useState<CostParams>({
    cottonCostPerKg: 2.50,
    spinningCostPerKg: 1.50,
    elastomerCostPerKg: 6.00,
    weavingCostPerMeter: 0.75,
    weavingWaste: 9,
    finishingWaste: 5,
    includeGeneralExpenses: true,
    generalExpensesPercentage: 18,
  });

  const [isDirty, setIsDirty] = useState(false);
  const [isSaveDialogOpen, setIsSaveDialogOpen] = useState(false);
  const [isAdvisorRunning, setIsAdvisorRunning] = useState(false);
  const [advisorContent, setAdvisorContent] = useState('');

  // === DATA FETCHING ===
  useEffect(() => {
    const loadInitialData = async () => {
      setIsLoading(true);
      try {
        const [articles, collections] = await Promise.all([
          api.fetchArticles(),
          api.fetchCollections(),
        ]);
        setAllArticles(articles);
        setAllCollections(collections);
      } catch (error) {
        console.error("Failed to load initial data", error);
      } finally {
        setIsLoading(false);
      }
    };
    loadInitialData();
  }, []);

  // === DERIVED STATE & MEMOS ===
  const isApiKeyMissing = false; // API key ora server-side

  const selectedCollection = useMemo(() => {
    return allCollections.find(c => c.id === selectedCollectionId);
  }, [selectedCollectionId, allCollections]);

  // Check for unsaved changes
  useEffect(() => {
    const currentIds = currentCollectionArticles.map(a => a.id).sort().join(',');
    const originalIds = originalCollectionArticles.sort().join(',');
    setIsDirty(currentIds !== originalIds);
  }, [currentCollectionArticles, originalCollectionArticles]);
  
  // Update collection articles when selection changes
  useEffect(() => {
    setInspectedArticleId(null);
    if (selectedCollectionId === 'new') {
        setCurrentCollectionArticles([]);
        setOriginalCollectionArticles([]);
    } else {
        const collection = allCollections.find(c => c.id === selectedCollectionId);
        if (collection) {
            const articles = collection.articles
                .map(id => allArticles.find(a => a.id === id))
                .filter((a): a is Article => a !== undefined);
            setCurrentCollectionArticles(articles);
            setOriginalCollectionArticles(collection.articles);
        }
    }
  }, [selectedCollectionId, allCollections, allArticles]);
  

  // === HANDLERS ===
   const handleInspectArticle = useCallback((articleId: string) => {
    setInspectedArticleId(prevId => prevId === articleId ? null : articleId);
  }, []);

  const handleShowCostDetail = useCallback((article: Article) => {
    const breakdown = calculateCostBreakdown(article, costParams);
    setCostModalState({ article, breakdown });
  }, [costParams]);

  const handleAddToCollection = useCallback((articlesToAdd: Article[]) => {
    setCurrentCollectionArticles(prev => {
      const existingIds = new Set(prev.map(a => a.id));
      const newArticles = articlesToAdd.filter(a => !existingIds.has(a.id));
      return [...prev, ...newArticles].sort((a,b) => a.name.localeCompare(b.name));
    });
  }, []);

  const handleRemoveFromCollection = useCallback((articlesToRemove: Article[]) => {
    setCurrentCollectionArticles(prev => {
      const idsToRemove = new Set(articlesToRemove.map(a => a.id));
      return prev.filter(a => !idsToRemove.has(a.id));
    });
    if (articlesToRemove.some(a => a.id === inspectedArticleId)) {
        setInspectedArticleId(null);
    }
  }, [inspectedArticleId]);

  const handleSelectCollection = useCallback((id: string) => {
    if (isDirty) {
      if (!window.confirm('Hai modifiche non salvate. Sei sicuro di voler cambiare collezione? Le modifiche andranno perse.')) {
        return;
      }
    }
    setSelectedCollectionId(id);
  }, [isDirty]);
  
  const handleSave = () => {
    if (selectedCollectionId === 'new') {
      setIsSaveDialogOpen(true);
    } else {
      handleSaveCollection(selectedCollection?.name || 'Unnamed Collection', selectedCollection?.isOfficial || false);
    }
  };

  const handleSaveCollection = useCallback(async (name: string, isOfficial: boolean) => {
    setIsLoading(true);
    setIsSaveDialogOpen(false);
    
    const collectionToSave = {
        name,
        isOfficial,
        articles: currentCollectionArticles.map(a => a.id),
        id: selectedCollectionId === 'new' ? undefined : selectedCollectionId
    };

    try {
        const saved = await api.saveCollection(collectionToSave);
        setAllCollections(prev => {
            const existing = prev.find(c => c.id === saved.id);
            if (existing) {
                return prev.map(c => c.id === saved.id ? saved : c);
            }
            return [...prev, saved];
        });
        setSelectedCollectionId(saved.id);
        setOriginalCollectionArticles(saved.articles);
        setIsDirty(false);
    } catch (error) {
        console.error("Failed to save collection", error);
    } finally {
        setIsLoading(false);
    }
  }, [currentCollectionArticles, selectedCollectionId]);

  const handleRunAdvisor = async () => {
    if (currentCollectionArticles.length === 0 && !isApiKeyMissing) {
      alert("Aggiungi articoli alla collezione prima di chiedere una consulenza.");
      return;
    }
    setIsAdvisorRunning(true);
    setAdvisorContent('');
    try {
      const stream = geminiService.getCollectionInsights(currentCollectionArticles);
      for await (const text of stream) {
        setAdvisorContent(prev => prev + text);
      }
    } catch (error) {
      console.error("Advisor Error:", error);
      setAdvisorContent("Errore durante la generazione dei suggerimenti.");
    } finally {
      setIsAdvisorRunning(false);
    }
  };
  
  // === RENDER LOGIC ===
  const renderTabContent = () => {
    switch(activeTab) {
      case 'collection':
        return (
          <div className="grid grid-cols-1 lg:grid-cols-11 gap-6 flex-grow">
            <div className="lg:col-span-5 xl:col-span-4">
              <ArticlePanel 
                allArticles={allArticles}
                collectionArticles={currentCollectionArticles}
                onAdd={handleAddToCollection}
                onInspect={handleInspectArticle}
                inspectedArticleId={inspectedArticleId}
              />
            </div>
            
            <div className="lg:col-span-1 flex lg:flex-col items-center justify-center gap-4">
               <TransferControls onAdd={handleAddToCollection} onRemove={handleRemoveFromCollection}/>
            </div>

            <div className="lg:col-span-5 xl:col-span-6">
              <CollectionPanel 
                articles={currentCollectionArticles}
                onRemove={handleRemoveFromCollection}
                isDirty={isDirty}
                onSave={handleSave}
                onInspect={handleInspectArticle}
                inspectedArticleId={inspectedArticleId}
              />
            </div>
          </div>
        );
      case 'costs':
        return (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="flex flex-col gap-6">
               <CostingPanel
                costParams={costParams}
                setCostParams={setCostParams}
              />
              <ResultsPanel 
                articles={currentCollectionArticles}
                costParams={costParams}
                onShowCostDetail={handleShowCostDetail}
              />
            </div>
            <div className="lg:col-span-1">
              <CostSimulator costParams={costParams} />
            </div>
          </div>
        );
      case 'advisor':
        return (
          <GeminiAdvisor 
            onRun={handleRunAdvisor}
            isRunning={isAdvisorRunning}
            content={advisorContent}
            hasArticles={currentCollectionArticles.length > 0}
            isApiKeyMissing={isApiKeyMissing}
          />
        );
      default:
        return null;
    }
  };


  return (
    <>
      {isLoading && <Loader fullScreen={true} />}
      <div className="min-h-screen p-4 sm:p-6 lg:p-8 bg-gray-100 flex flex-col gap-6">
        <Header 
          collections={allCollections}
          selectedCollectionId={selectedCollectionId}
          onSelectCollection={handleSelectCollection}
        />

        <div className="w-full bg-white rounded-lg shadow-md p-2 flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
            <TabButton 
                label="Gestione Collezione"
                icon="fa-layer-group"
                isActive={activeTab === 'collection'}
                onClick={() => setActiveTab('collection')}
            />
            <TabButton 
                label="Analisi Costi"
                icon="fa-calculator"
                isActive={activeTab === 'costs'}
                onClick={() => setActiveTab('costs')}
            />
            <TabButton 
                label="Consulente AI"
                icon="fa-magic-wand-sparkles"
                isActive={activeTab === 'advisor'}
                onClick={() => setActiveTab('advisor')}
            />
        </div>

        <main className="flex-grow flex flex-col">
          {renderTabContent()}
        </main>
        
        {isSaveDialogOpen && (
          <SaveCollectionDialog
            onClose={() => setIsSaveDialogOpen(false)}
            onSave={handleSaveCollection}
          />
        )}
        {costModalState && (
          <CostDetailModal
            breakdown={costModalState.breakdown}
            onClose={() => setCostModalState(null)}
          />
        )}
      </div>
    </>
  );
}

export default App;
